/**
 * /timesheet — top-level timesheet view.
 *
 * URL search params drive the three persistent filters so a hard reload
 * (or a shared link) lands the user on the same surface:
 *   - `scope` : 'day' | 'week'        (default 'day')
 *   - `mode`  : 'calendar' | 'list'   (default 'calendar')
 *   - `date`  : 'YYYY-MM-DD'          (default today; the anchor — for
 *                                       scope=week, the rendered range
 *                                       is the Mon-Sun containing this
 *                                       date)
 *
 * Param-name note: `scope` rather than the more obvious `view` to avoid
 * collision with the `view` search param used by the master dashboard
 * (`unassigned | review | team-work | my-work`) and board page surfaces.
 * TanStack Router's global search-params registry unions same-named keys
 * across routes, so a `view: 'day'|'week'` here would leak into every
 * other route's typing and break their (prev) => … navigate reducers.
 *
 * Auth gate mirrors `routes/index.tsx` and `routes/board/$projectId.tsx`:
 * no access token → bounce to /login before kicking off the API roundtrip.
 *
 * Guests get a second, separate bounce — to `/`, not `/login`. Time
 * tracking is entirely outside the guest ceiling: every route this page
 * calls refuses a guest, so without the guard a guest who types the URL
 * (the nav entry is hidden for them) lands on a page whose every fetch
 * 403s. They are legitimately signed in, so the destination is the
 * dashboard they DO have, not the sign-in page.
 *
 * The guest guard also HOLDS the page until `/api/me` answers instead of
 * rendering optimistically. `useIsGuest()` reads NOT-guest while that is
 * in flight (deliberate, so a member's shell never flashes reduced), and
 * on a cold load of this URL that window is long enough for the page to
 * mount and fire its three time-tracking fetches — all 403 — before the
 * redirect lands. Waiting is not the same as bouncing on unknown: an
 * unresolved identity still never navigates anyone away. On an in-app
 * navigation `me` is already cached by the shell, so this costs nothing.
 *
 * The actual page UI lives in `features/time/components/TimesheetPage`.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { useIsGuest } from '@/features/me/guest'
import { useMe } from '@/features/me/use-me'
import { TimesheetPage } from '@/features/time/components/TimesheetPage'

export type TimesheetScope = 'day' | 'week'
export type TimesheetMode = 'calendar' | 'list'

export interface TimesheetSearchParams {
  scope?: TimesheetScope
  mode?: TimesheetMode
  date?: string
}

const SCOPE_VALUES: readonly TimesheetScope[] = ['day', 'week']
const MODE_VALUES: readonly TimesheetMode[] = ['calendar', 'list']
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export const Route = createFileRoute('/timesheet')({
  component: TimesheetRoute,
  validateSearch: (search: Record<string, unknown>): TimesheetSearchParams => {
    const out: TimesheetSearchParams = {}
    const s = search.scope
    if (typeof s === 'string' && (SCOPE_VALUES as readonly string[]).includes(s)) {
      out.scope = s as TimesheetScope
    }
    const m = search.mode
    if (typeof m === 'string' && (MODE_VALUES as readonly string[]).includes(m)) {
      out.mode = m as TimesheetMode
    }
    const d = search.date
    if (typeof d === 'string' && ISO_DATE_RE.test(d)) {
      out.date = d
    }
    return out
  },
})

function TimesheetRoute() {
  const navigate = useNavigate()
  // `useIsGuest()` falls open to NOT-guest while `/api/me` is in flight,
  // which is what makes this guard safe to run alongside the token one:
  // an unresolved identity never bounces a member off their own page.
  const me = useMe()
  const isGuest = useIsGuest()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  useEffect(() => {
    if (isGuest) {
      navigate({ to: '/' })
    }
  }, [isGuest, navigate])

  if (!getAccessToken()) return null
  if (me.isPending || isGuest) return null

  return <TimesheetPage />
}
